import React, { useState, useEffect, useRef, useCallback } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import {
  Clock, BookmarkCheck, ArrowLeft, ArrowRight, RotateCcw,
  Send, CheckCircle2, X, Calculator as CalcIcon, Lock, Layers,
  GripVertical, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Bookmark, Check, Compass, BookOpen, HelpCircle,
  AlertCircle, FileText
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { Toaster } from '@/components/ui/toaster'
import { KatexRenderer, parseQuestionDirectionAndPrompt } from '@/components/shared/KatexRenderer'
import { QuestionContextCard, QuestionContextData, cleanContextTitle } from '@/components/shared/QuestionContextCard'
import { CalculatorWidget } from '@/components/shared/CalculatorWidget'

interface Option {
  id: number
  label: string
  text: string | null
  option_image_url?: string | null
}

interface Question {
  id: number
  question_text: string
  question_image_url?: string | null
  figure_image_url?: string | null
  render_format: string
  difficulty: string
  question_type: string
  positive_marks: number
  negative_marks: number
  section_id?: number | null
  question_context?: QuestionContextData | null
  options: Option[]
}

interface SectionMeta {
  id: number
  name: string
  total_questions: number
  max_attempts: number | null
  duration_minutes: number | null
  subject_hint: string | null
  marking_rule?: {
    name: string
    strategy: string
    params: Record<string, any>
  } | null
}

interface Attempt {
  id: number
  attempt_number: number
  remaining_seconds: number | null
  expires_at?: string | null
  section_states?: Record<string, { remaining_seconds?: number; locked?: boolean; last_question_index?: number }>
  active_section_id?: number | null
  test: {
    id: number
    title: string
    duration_minutes: number | null
    language: string
    test_type: string
    total_questions?: number
    max_attempts?: number | null
    sections?: SectionMeta[]
    exam_config?: {
      timer_type?: string
      calculator_enabled?: boolean
      marked_for_review_rule?: string
      allow_language_switch?: boolean
      general_instructions_html?: string
    } | null
  }
  saved_answers: Array<{
    question_id: number
    selected_option_ids?: number[]
    selected_option_id?: number | null
    numerical_response: number | null
    marked_for_review: boolean
    review_status?: string
    section_id?: number | null
    time_spent_seconds?: number
  }>
}

interface Props {
  attempt: Attempt
  questions: Question[]
}

interface AnswerState {
  selected_option_ids: number[]
  numerical_response: number | null
  marked_for_review: boolean
  review_status: string
  visited: boolean
  section_id?: number | null
  time_spent_seconds?: number
}

const formatTime = (seconds: number) => {
  if (!seconds || isNaN(seconds) || seconds < 0) return '00:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// TimerDisplay — pure display component, expiry is handled by parent via sectionTimers watcher
function TimerDisplay({
  remainingSeconds,
  sectionName,
  isSectional = false,
  isActiveSection = true,
}: {
  remainingSeconds: number
  sectionName?: string | null
  isSectional?: boolean
  isActiveSection?: boolean
}) {
  const isCritical = remainingSeconds <= 60 && remainingSeconds > 0 // Below 1 min -> Red
  const isWarning = remainingSeconds <= 180 && remainingSeconds > 60 // 1 to 3 mins -> Amber/Tertiary
  const isExpired = remainingSeconds <= 0

  return (
    <div className={`flex items-center gap-1.5 px-3 py-1 sm:px-3.5 sm:py-1 rounded-full border font-mono text-xs sm:text-sm font-bold transition-all shadow-2xs ${
      !isActiveSection || isExpired
        ? 'bg-muted/40 border-border/70 text-muted-foreground opacity-70'
        : isCritical
        ? 'border-destructive bg-destructive/15 text-destructive animate-pulse'
        : isWarning
        ? 'border-tertiary/40 bg-tertiary/10 text-tertiary ring-1 ring-tertiary/30'
        : 'bg-card border-primary/40 text-primary ring-1 ring-primary/30'
    }`}>
      <Clock className={`h-3.5 w-3.5 ${
        !isActiveSection || isExpired
          ? 'text-muted-foreground'
          : isCritical
          ? 'text-destructive'
          : isWarning
          ? 'text-tertiary'
          : 'text-primary'
      }`} />
      {sectionName && (
        <span className="text-[11px] font-sans font-semibold text-foreground mr-0.5 max-w-[110px] truncate">
          {sectionName}:
        </span>
      )}
      <span className="tabular-nums">{formatTime(remainingSeconds)}</span>
      {isSectional && isActiveSection && !isExpired && (
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary animate-ping ml-0.5" title="Active Section Timer" />
      )}
    </div>
  )
}

// Top-level Memoized PaletteGrid component with automatic smooth scrolling to active question
const PaletteGrid = React.memo(function PaletteGrid({
  questions,
  sections,
  currentIndex,
  answers: _answers,
  answeredCount,
  unansweredCount,
  markedCount,
  notVisitedCount,
  getPaletteColor,
  onSelectQuestion,
  lockedSectionIds = new Set(),
  isMobile = false,
  maxAttempts = null
}: {
  questions: Question[]
  sections?: SectionMeta[]
  currentIndex: number
  answers: Record<number, AnswerState>
  answeredCount: number
  unansweredCount: number
  markedCount: number
  notVisitedCount: number
  getPaletteColor: (q: Question) => string
  onSelectQuestion: (idx: number) => void
  lockedSectionIds?: Set<number>
  isMobile?: boolean
  maxAttempts?: number | null
}) {
  const activeBtnRef = useRef<HTMLButtonElement | null>(null)

  // Smoothly scroll active button into view when currentIndex changes (e.g. Next / Prev buttons or direct click)
  useEffect(() => {
    if (activeBtnRef.current) {
      activeBtnRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      })
    }
  }, [currentIndex])

  // Build section-grouped palette items: separator row + question buttons per section
  const hasSections = sections && sections.length > 0 && questions.some(q => q.section_id != null)

  const paletteItems = hasSections
    ? sections!.flatMap(sec => {
        const secQs = questions.filter(q => q.section_id === sec.id)
        if (secQs.length === 0) return []
        const secLocked = lockedSectionIds.has(sec.id)
        return [
          // Section separator — spans all 5 columns via col-span-5
          <div
            key={`sep-${sec.id}`}
            className="col-span-5 flex items-center gap-2 pt-1 pb-0.5"
          >
            <span className={`text-[10px] font-bold uppercase tracking-widest shrink-0 ${secLocked ? 'text-destructive/70' : 'text-primary/70'}`}>
              {sec.name}
            </span>
            {secLocked && (
              <Lock className="h-2.5 w-2.5 text-destructive/70 shrink-0" />
            )}
            <span className="flex-1 h-px bg-border/60" />
            <span className="text-[10px] text-muted-foreground shrink-0">{secQs.length} Qs</span>
          </div>,
          ...secQs.map(q => {
            const idx = questions.indexOf(q)
            const isCurrent = idx === currentIndex
            return (
              <button
                key={q.id}
                ref={isCurrent ? activeBtnRef : null}
                disabled={secLocked}
                onClick={() => !secLocked && onSelectQuestion(idx)}
                title={secLocked ? 'Section locked — time expired' : undefined}
                className={`h-9 sm:h-10 w-full rounded-lg text-xs sm:text-sm font-bold transition-all flex items-center justify-center relative ${
                  secLocked
                    ? 'bg-muted/30 text-muted-foreground/40 border border-border/30 cursor-not-allowed opacity-50'
                    : `${getPaletteColor(q)} cursor-pointer ${isCurrent ? 'ring-2 ring-primary font-extrabold shadow-xs' : 'hover:opacity-90'}`
                }`}
              >
                {idx + 1}
              </button>
            )
          })
        ]
      })
    : questions.map((q, idx) => {
        const isCurrent = idx === currentIndex
        return (
          <button
            key={q.id}
            ref={isCurrent ? activeBtnRef : null}
            onClick={() => onSelectQuestion(idx)}
            className={`h-9 sm:h-10 w-full rounded-lg text-xs sm:text-sm font-bold transition-all flex items-center justify-center cursor-pointer ${getPaletteColor(q)} ${
              isCurrent ? 'ring-2 ring-primary font-extrabold shadow-xs' : 'hover:opacity-90'
            }`}
          >
            {idx + 1}
          </button>
        )
      })

  return (
    <div className="flex flex-col h-full space-y-3 sm:space-y-4 min-h-0">
      <div className="grid grid-cols-2 gap-1.5 text-xs shrink-0">
        <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/25">
          <span className="h-3 w-3 rounded-md bg-primary text-[9px] font-bold text-primary-foreground flex items-center justify-center shrink-0">✓</span>
          <span className="font-semibold text-xs text-foreground truncate">Answered ({answeredCount}{maxAttempts ? ` / ${maxAttempts}` : ''})</span>
        </div>
        <div className="flex items-center gap-2 p-2 rounded-lg bg-tertiary/10 border border-tertiary/25">
          <span className="h-3 w-3 rounded-md bg-tertiary/20 border border-tertiary/50 text-[9px] font-bold text-tertiary flex items-center justify-center shrink-0">?</span>
          <span className="font-semibold text-xs text-foreground truncate">Unanswered ({unansweredCount > 0 ? unansweredCount : 0})</span>
        </div>
        <div className="flex items-center gap-2 p-2 rounded-lg bg-secondary/10 border border-secondary/25">
          <span className="h-3 w-3 rounded-md bg-secondary text-[9px] font-bold text-secondary-foreground flex items-center justify-center shrink-0">★</span>
          <span className="font-semibold text-xs text-foreground truncate">Marked ({markedCount})</span>
        </div>
        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/40 border border-border/50">
          <span className="h-3 w-3 rounded-md bg-muted border border-border/70 text-[9px] text-muted-foreground flex items-center justify-center shrink-0">-</span>
          <span className="font-semibold text-xs text-muted-foreground truncate">Not Visited ({notVisitedCount})</span>
        </div>
      </div>

      <div className={`grid grid-cols-5 gap-2 sm:gap-2.5 p-1 overflow-y-auto min-h-0 content-start auto-rows-max ${isMobile ? 'max-h-[52vh]' : 'flex-1'}`}>
        {paletteItems}
      </div>
    </div>
  )
})

export default function StudentAttemptShow({ attempt, questions }: Props) {
  const { toast } = useToast()

  // Restore last position: use persisted active_section_id + last_question_index from section_states
  const [currentIndex, setCurrentIndex] = useState(() => {
    const savedSectionStates = attempt.section_states || {}
    let activeSectionId = attempt.active_section_id

    // If active section is locked or has 0 seconds, auto-find next unlocked section with time
    if (activeSectionId != null) {
      const state = savedSectionStates[String(activeSectionId)]
      const isLocked = state?.locked || (state?.remaining_seconds != null && state.remaining_seconds <= 0)
      if (isLocked) {
        const nextSec = (attempt.test.sections || []).find(sec => {
          const s = savedSectionStates[String(sec.id)]
          const rem = s?.remaining_seconds != null ? s.remaining_seconds : ((sec.duration_minutes || 0) * 60)
          return !s?.locked && rem > 0
        })
        if (nextSec) {
          activeSectionId = nextSec.id
        }
      }
    }

    if (activeSectionId != null) {
      // Try to resume the exact question index within the active section
      const savedIdx = savedSectionStates[String(activeSectionId)]?.last_question_index
      if (savedIdx != null && savedIdx >= 0 && savedIdx < questions.length) {
        // Verify this index actually belongs to the active section
        if (questions[savedIdx]?.section_id === activeSectionId) {
          return savedIdx
        }
      }
      // Fallback: find the first question of the active section
      const firstInSection = questions.findIndex(q => q.section_id === activeSectionId)
      if (firstInSection !== -1) return firstInSection
    }

    // Non-sectional or no active section: fall back to first question
    return 0
  })

  const safeCurrentIndex = (currentIndex >= 0 && currentIndex < questions.length)
    ? currentIndex
    : Math.max(0, Math.min(questions.length - 1, currentIndex || 0))
  const currentQuestion = questions[safeCurrentIndex]
  const currentSectionId = currentQuestion?.section_id || (attempt.test.sections && attempt.test.sections[0]?.id) || null
  const currentSection = (attempt.test.sections || []).find(s => s.id === currentSectionId)
  const isUntimed =
    attempt.test.exam_config?.timer_type === 'none' ||
    attempt.test.test_type === 'practice' ||
    attempt.test.duration_minutes == null ||
    attempt.test.duration_minutes === 0 ||
    attempt.test.title.toLowerCase().includes('practice') ||
    (!attempt.expires_at &&
      !attempt.test.duration_minutes &&
      !(attempt.test.sections || []).some(s => (s.duration_minutes || 0) > 0))
  const hasSectionDurations = (attempt.test.sections || []).some(s => (s.duration_minutes || 0) > 0)
  const isSectional =
    !isUntimed &&
    attempt.test.exam_config?.timer_type !== 'global' &&
    (attempt.test.exam_config?.timer_type === 'sectional' || hasSectionDurations) &&
    hasSectionDurations
  const isSectionAttemptCapped = Boolean(
    currentSection?.max_attempts &&
    currentSection.max_attempts > 0 &&
    currentSection.max_attempts < currentSection.total_questions
  )
  const isTestAttemptCapped = Boolean(
    attempt.test?.max_attempts &&
    attempt.test.max_attempts > 0 &&
    attempt.test.max_attempts < questions.length
  )

  // Initialize section-wise remaining seconds map
  const [sectionTimers, setSectionTimers] = useState<Record<string, number | null>>(() => {
    const map: Record<string, number | null> = {}
    ;(attempt.test.sections || []).forEach(sec => {
      if (isUntimed) {
        map[String(sec.id)] = null
        return
      }
      const saved = attempt.section_states?.[String(sec.id)]?.remaining_seconds
      map[String(sec.id)] = saved != null ? saved : (sec.duration_minutes ? sec.duration_minutes * 60 : null)
    })
    return map
  })

  // Global remaining seconds
  const [globalRemaining, setGlobalRemaining] = useState<number | null>(() => {
    if (isUntimed) return null
    if (attempt.remaining_seconds != null && !isNaN(attempt.remaining_seconds)) {
      return attempt.remaining_seconds
    }
    if (attempt.expires_at) {
      const diff = Math.floor((new Date(attempt.expires_at).getTime() - new Date().getTime()) / 1000)
      return Math.max(diff, 0)
    }
    return attempt.test.duration_minutes ? attempt.test.duration_minutes * 60 : null
  })

  const [submitDialogOpen, setSubmitDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [paletteDrawerOpen, setPaletteDrawerOpen] = useState(false)
  const [selectedPaletteIndex, setSelectedPaletteIndex] = useState<number>(safeCurrentIndex)
  const [drawerSectionId, setDrawerSectionId] = useState<number | null>(null)
  const [expandedUnvisitedRanges, setExpandedUnvisitedRanges] = useState<Set<string>>(new Set())
  const [directionsOpen, setDirectionsOpen] = useState(false)
  const [mobileRcTab, setMobileRcTab] = useState<'question' | 'passage'>('question')

  // Keep selectedPaletteIndex and mobileRcTab aligned with currentIndex
  useEffect(() => {
    if (currentIndex >= 0 && currentIndex < questions.length) {
      setSelectedPaletteIndex(currentIndex)
    }
    setMobileRcTab('question')
  }, [currentIndex, questions.length])

  // Auto-scroll active section tab button into view on section change
  const sectionTabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  useEffect(() => {
    if (currentQuestion?.section_id != null) {
      const activeBtn = sectionTabRefs.current[String(currentQuestion.section_id)]
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
      }
    }
  }, [currentQuestion?.section_id])

  // Countdown timer effect
  useEffect(() => {
    if (isUntimed) return

    const timer = setInterval(() => {
      if (isSectional && currentSectionId) {
        setSectionTimers(prev => {
          const currentVal = prev[String(currentSectionId)]
          if (currentVal == null) return prev
          if (currentVal <= 1) {
            return { ...prev, [String(currentSectionId)]: 0 }
          }
          return { ...prev, [String(currentSectionId)]: currentVal - 1 }
        })
      } else {
        setGlobalRemaining(prev => {
          if (prev == null) return null
          if (prev <= 1) {
            return 0
          }
          return prev - 1
        })
      }
    }, 1000)
    return () => clearInterval(timer)
  }, [isUntimed, isSectional, currentSectionId, currentSection?.duration_minutes])

  // Keep a ref to sectionTimers so saveAnswers always reads the latest countdown value without recreation
  const sectionTimersRef = useRef<Record<string, number | null>>(sectionTimers)
  useEffect(() => {
    sectionTimersRef.current = sectionTimers
  }, [sectionTimers])

  // Track last-visited question index per section (keyed by section_id string)
  // Initialized from persisted section_states.last_question_index
  const sectionLastIndexRef = useRef<Record<string, number>>((() => {
    const map: Record<string, number> = {}
    const savedStates = attempt.section_states || {}
    ;(attempt.test.sections || []).forEach(sec => {
      const savedIdx = savedStates[String(sec.id)]?.last_question_index
      if (savedIdx != null) map[String(sec.id)] = savedIdx
    })
    return map
  })())

  // Sync sectionLastIndexRef and currentSectionIdRef whenever currentIndex changes
  useEffect(() => {
    const secId = questions[currentIndex]?.section_id
    if (secId != null) {
      sectionLastIndexRef.current[String(secId)] = currentIndex
      currentSectionIdRef.current = secId
    }
  }, [currentIndex, questions])

  // Track time spent in seconds per question
  const questionTimeSpentRef = useRef<Record<number, number>>((() => {
    const map: Record<number, number> = {}
    ;(attempt.saved_answers || []).forEach(sa => {
      if (sa.time_spent_seconds != null) {
        map[sa.question_id] = sa.time_spent_seconds
      }
    })
    return map
  })())

  // Accumulate time spent on active question every second
  useEffect(() => {
    const timer = setInterval(() => {
      const activeQ = questions[currentIndex]
      if (activeQ) {
        questionTimeSpentRef.current[activeQ.id] = (questionTimeSpentRef.current[activeQ.id] || 0) + 1
      }
    }, 1000)
    return () => clearInterval(timer)
  }, [currentIndex, questions])

  // Initialize answer map
  const [answers, setAnswers] = useState<Record<number, AnswerState>>(() => {
    const map: Record<number, AnswerState> = {}
    ;(attempt.saved_answers || []).forEach((sa) => {
      const optionIds = sa.selected_option_ids && sa.selected_option_ids.length > 0
        ? sa.selected_option_ids
        : (sa.selected_option_id != null ? [sa.selected_option_id] : [])

      map[sa.question_id] = {
        selected_option_ids: optionIds,
        numerical_response: sa.numerical_response,
        marked_for_review: sa.marked_for_review,
        review_status: sa.review_status || (sa.marked_for_review ? 'marked_for_review_with_answer' : 'not_marked'),
        visited: optionIds.length > 0 || sa.numerical_response != null || sa.marked_for_review,
        section_id: sa.section_id,
        time_spent_seconds: sa.time_spent_seconds || 0
      }
    })
    return map
  })

  const [showCalculator, setShowCalculator] = useState(false)
  const [showInstructionsModal, setShowInstructionsModal] = useState(false)
  const [sectionStates, setSectionStates] = useState<Record<string, { remaining_seconds?: number; locked?: boolean; last_question_index?: number }>>(
    attempt.section_states || {}
  )

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingQueue = useRef<Record<number, AnswerState>>({})
  // Always holds the current section id so saveAnswers can read it without stale closures
  const currentSectionIdRef = useRef<number | null>(
    attempt.active_section_id ?? (attempt.test.sections?.[0]?.id ?? null)
  )

  // Determine context and question prompt: either from explicit question_context or extracted directions
  const extractedContext = React.useMemo(() => {
    if (currentQuestion?.question_context) {
      return {
        context: currentQuestion.question_context,
        promptText: currentQuestion.question_text,
      }
    }
    if (!currentQuestion?.question_text) {
      return { context: null, promptText: '' }
    }
    const parsed = parseQuestionDirectionAndPrompt(currentQuestion.question_text)
    if (parsed.direction || parsed.passage) {
      return {
        context: {
          external_id: '',
          context_type: parsed.contextType || (parsed.passage ? 'reading_comprehension' : 'Directions'),
          direction_text: parsed.direction || 'Directions: Refer to the context below.',
          body_text: parsed.passage || null,
        } as QuestionContextData,
        promptText: parsed.prompt,
      }
    }
    return { context: null, promptText: currentQuestion.question_text }
  }, [currentQuestion])

  const hasContext = Boolean(extractedContext.context)
  const displayedContext = extractedContext.context
  const effectiveQuestionText = extractedContext.promptText

  // Categorize context for mobile: Passage/RC vs Directions-only
  // A context has a passage/stimulus if it has body text, diagram image, audio, or chart data
  const hasPassageContext = Boolean(
    displayedContext &&
    (
      (displayedContext.body_text && displayedContext.body_text.trim().length > 0) ||
      Boolean(displayedContext.context_image_url) ||
      Boolean(displayedContext.media_url) ||
      Boolean(displayedContext.chart_data && Object.keys(displayedContext.chart_data).length > 0)
    )
  )

  // A context is directions-only if it has instructions or direction text but NO passage body, diagram, or media
  const hasDirectionsOnly = Boolean(
    displayedContext &&
    !hasPassageContext &&
    Boolean(
      (displayedContext.direction_text && displayedContext.direction_text.trim().length > 0) ||
      (displayedContext.body_text && displayedContext.body_text.trim().length > 0)
    )
  )

  const effectiveDirectionText = displayedContext?.direction_text || displayedContext?.body_text || ''

  const passageSnippet = React.useMemo(() => {
    if (!displayedContext?.body_text) return ''
    const clean = displayedContext.body_text
      .replace(/[#*`_\[\]]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
    return clean.length > 60 ? `${clean.slice(0, 58)}...` : clean
  }, [displayedContext?.body_text])

  const formatContextType = (type?: string | null) => {
    switch (type?.toLowerCase()) {
      case 'reading_comprehension':
      case 'rc':
      case 'passage':
        return 'Reading comprehension'
      case 'cloze_test':
        return 'Cloze test'
      case 'data_interpretation':
      case 'di':
        return 'Data interpretation'
      case 'puzzle':
        return 'Logical puzzle'
      case 'caselet':
        return 'Caselet'
      default:
        if (hasPassageContext) return 'Reading comprehension'
        return 'Directions'
    }
  }

  // Periodic and autosave function syncing section timers and question time spent
  const saveAnswers = useCallback(async () => {
    // Ensure active question has latest time in queue
    if (currentQuestion) {
      const curr = answers[currentQuestion.id] || {
        selected_option_ids: [],
        numerical_response: null,
        marked_for_review: false,
        review_status: 'not_marked',
        visited: true,
        section_id: currentQuestion.section_id
      }
      pendingQueue.current[currentQuestion.id] = {
        ...curr,
        time_spent_seconds: questionTimeSpentRef.current[currentQuestion.id] || 0
      }
    }

    const payload = Object.entries(pendingQueue.current).map(([qId, state]) => ({
      question_id: Number(qId),
      selected_option_ids: state.selected_option_ids,
      selected_option_id: state.selected_option_ids[0] ?? null,
      numerical_response: state.numerical_response,
      marked_for_review: state.marked_for_review,
      review_status: state.review_status,
      test_paper_section_id: state.section_id,
      visited: state.visited,
      time_spent_seconds: questionTimeSpentRef.current[Number(qId)] ?? state.time_spent_seconds ?? 0,
    }))
    pendingQueue.current = {}

    // Merge latest section timer remaining values + last visited question index per section
    const mergedSectionStates: Record<string, any> = { ...(attempt.section_states || {}) }
    Object.entries(sectionLastIndexRef.current).forEach(([secId, lastIdx]) => {
      mergedSectionStates[secId] = {
        ...(mergedSectionStates[secId] || {}),
        last_question_index: lastIdx
      }
    })
    if (isSectional) {
      Object.entries(sectionTimersRef.current).forEach(([secId, remSecs]) => {
        mergedSectionStates[secId] = {
          ...(mergedSectionStates[secId] || {}),
          remaining_seconds: remSecs
        }
      })
    }

    // Derive active_section_id from ref (always up-to-date, no stale closure issues)
    const activeSectionId = currentSectionIdRef.current

    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    try {
      await fetch(`/student/attempts/${attempt.id}/answers`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body: JSON.stringify({ answers: payload, section_states: mergedSectionStates, active_section_id: activeSectionId }),
      })
    } catch (e) {
      console.error('Failed to autosave answers', e)
    }
  }, [attempt.id, attempt.section_states, isSectional, currentQuestion, answers])

  // Periodic 5-second background autosave to persist timer countdown and per-question time spent to backend
  useEffect(() => {
    const syncInterval = setInterval(() => {
      saveAnswers()
    }, 5000)
    return () => clearInterval(syncInterval)
  }, [saveAnswers])

  // Mark visited
  useEffect(() => {
    if (currentQuestion && !answers[currentQuestion.id]?.visited) {
      const current = answers[currentQuestion.id] || {
        selected_option_ids: [],
        numerical_response: null,
        marked_for_review: false,
        review_status: 'not_marked',
        section_id: currentQuestion.section_id
      }
      setAnswers((prev) => ({
        ...prev,
        [currentQuestion.id]: {
          ...current,
          visited: true,
        }
      }))
    }
  }, [currentIndex, currentQuestion])

  const scheduleAutosave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(saveAnswers, 400)
  }, [saveAnswers])

  const updateAnswer = (questionId: number, patch: Partial<AnswerState>) => {
    setAnswers((prev) => {
      const current = prev[questionId] || {
        selected_option_ids: [],
        numerical_response: null,
        marked_for_review: false,
        review_status: 'not_marked',
        visited: true,
        section_id: currentQuestion?.section_id
      }
      const updated = { ...current, ...patch, visited: true }
      pendingQueue.current[questionId] = updated
      return { ...prev, [questionId]: updated }
    })
    scheduleAutosave()
  }

  const isQuestionAnswered = useCallback((qId: number) => {
    const a = answers[qId]
    if (!a) return false
    return (a.selected_option_ids?.length ?? 0) > 0 || (a.numerical_response != null && !isNaN(a.numerical_response))
  }, [answers])

  const answeredCount = React.useMemo(() => {
    return questions.filter(q => isQuestionAnswered(q.id)).length
  }, [questions, isQuestionAnswered])

  const sectionAnsweredCount = React.useMemo(() => {
    if (!currentSection) return 0
    return questions.filter(q => q.section_id === currentSection.id && isQuestionAnswered(q.id)).length
  }, [currentSection, questions, isQuestionAnswered])

  const canAnswerCurrentQuestion = useCallback(() => {
    if (!currentQuestion) return true
    if (isQuestionAnswered(currentQuestion.id)) return true

    // 1. Enforce section-level attempt limit if configured for this section
    if (isSectionAttemptCapped && currentSection) {
      if (sectionAnsweredCount >= currentSection.max_attempts!) {
        toast({
          title: `Section attempt limit reached (${currentSection.max_attempts} of ${currentSection.max_attempts})`,
          description: `You can only attempt a maximum of ${currentSection.max_attempts} questions in ${currentSection.name}. To attempt this question, click "Clear Response" on an answered question in this section first.`,
          variant: 'destructive',
          duration: 6000,
        })
        return false
      }
    }

    // 2. Enforce test-level attempt limit if configured across the entire test
    if (isTestAttemptCapped && attempt.test.max_attempts) {
      if (answeredCount >= attempt.test.max_attempts) {
        toast({
          title: `Test attempt limit reached (${attempt.test.max_attempts} of ${attempt.test.max_attempts})`,
          description: `You can only attempt a maximum of ${attempt.test.max_attempts} questions in this test. To attempt this question, click "Clear Response" on an answered question first.`,
          variant: 'destructive',
          duration: 6000,
        })
        return false
      }
    }

    return true
  }, [isSectionAttemptCapped, currentSection, currentQuestion, isQuestionAnswered, sectionAnsweredCount, isTestAttemptCapped, attempt.test.max_attempts, answeredCount, toast])

  const handleSelectOption = (optionId: number) => {
    if (!currentQuestion) return
    const current = answers[currentQuestion.id] || { selected_option_ids: [] }
    const isCurrentlyAnswered = (current.selected_option_ids && current.selected_option_ids.length > 0)

    if (currentQuestion.question_type === 'MULTI_CORRECT') {
      const exists = current.selected_option_ids.includes(optionId)
      if (!exists && !isCurrentlyAnswered && !canAnswerCurrentQuestion()) return
      const next = exists
        ? current.selected_option_ids.filter(id => id !== optionId)
        : [...current.selected_option_ids, optionId]

      updateAnswer(currentQuestion.id, {
        selected_option_ids: next,
        review_status: current.marked_for_review
          ? (next.length > 0 ? 'marked_for_review_with_answer' : 'marked_for_review_only')
          : 'not_marked'
      })
    } else {
      const isSelected = current.selected_option_ids.includes(optionId)
      if (!isSelected && !isCurrentlyAnswered && !canAnswerCurrentQuestion()) return
      const next = isSelected ? [] : [optionId]
      updateAnswer(currentQuestion.id, {
        selected_option_ids: next,
        review_status: current.marked_for_review
          ? (next.length > 0 ? 'marked_for_review_with_answer' : 'marked_for_review_only')
          : 'not_marked'
      })
    }
  }

  const handleClearResponse = () => {
    if (!currentQuestion) return
    const current = answers[currentQuestion.id]
    updateAnswer(currentQuestion.id, {
      selected_option_ids: [],
      numerical_response: null,
      review_status: current?.marked_for_review ? 'marked_for_review_only' : 'not_marked'
    })
  }

  const handleToggleReview = () => {
    if (!currentQuestion) return
    const isMarked = !!answers[currentQuestion.id]?.marked_for_review
    const nextMarked = !isMarked
    const hasAns = (answers[currentQuestion.id]?.selected_option_ids?.length ?? 0) > 0 || answers[currentQuestion.id]?.numerical_response != null
    updateAnswer(currentQuestion.id, {
      marked_for_review: nextMarked,
      review_status: nextMarked
        ? (hasAns ? 'marked_for_review_with_answer' : 'marked_for_review_only')
        : 'not_marked'
    })
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    // Queue all questions that have accumulated time spent or existing answers to ensure final sync
    questions.forEach(q => {
      const time = questionTimeSpentRef.current[q.id] || 0
      const a = answers[q.id]
      if (time > 0 || a) {
        pendingQueue.current[q.id] = {
          ...(a || {
            selected_option_ids: [],
            numerical_response: null,
            marked_for_review: false,
            review_status: 'not_marked',
            visited: true,
            section_id: q.section_id
          }),
          time_spent_seconds: time
        }
      }
    })
    await saveAnswers()
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    try {
      const res = await fetch(`/student/attempts/${attempt.id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
      })
      if (res.ok) {
        toast({
          title: 'Test submitted!',
          description: 'Your attempt has been submitted successfully.',
        })
        setSubmitDialogOpen(false)
        router.visit(`/student/attempts/${attempt.id}/result`)
      } else {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to submit test. Please try again.',
        })
        setSubmitting(false)
      }
    } catch (e) {
      console.error(e)
      setSubmitting(false)
    }
  }

  // Called when a sectional timer hits 0: locks the section, switches to next available, or auto-submits
  const handleSectionExpire = useCallback(async (expiredSectionId: number) => {
    const expiredName = attempt.test.sections?.find(s => s.id === expiredSectionId)?.name ?? `Section ${expiredSectionId}`

    // 1. Lock section in local state immediately
    setSectionStates(prev => ({
      ...prev,
      [String(expiredSectionId)]: { ...(prev[String(expiredSectionId)] || {}), locked: true }
    }))

    // 2. Persist lock to backend
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    try {
      await fetch(`/student/attempts/${attempt.id}/lock_section`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body: JSON.stringify({ section_id: expiredSectionId }),
      })
    } catch (e) {
      console.error('Failed to lock section', e)
    }

    toast({
      title: (
        <span className="flex items-center gap-1.5 font-medium text-xs text-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span>{expiredName} time up — section locked</span>
        </span>
      ) as any,
      duration: 5000,
      className: 'h-8 sm:h-9 py-0 px-3.5 pr-8 border border-border bg-card/95 backdrop-blur-md shadow-xs rounded-full flex items-center gap-2 text-xs whitespace-nowrap',
    })

    // 3. Find next section that still has time remaining (excluding just-expired and already-locked)
    const sections = attempt.test.sections ?? []
    const nextSection = sections.find(sec => {
      if (sec.id === expiredSectionId) return false
      if (sectionStates[String(sec.id)]?.locked) return false
      const remSecs = sectionTimersRef.current[String(sec.id)] ?? ((sec.duration_minutes || 0) * 60)
      return remSecs > 0
    })

    if (nextSection) {
      // Switch to the first question of the next available section
      const lastIdx = sectionLastIndexRef.current[String(nextSection.id)]
      const targetIdx =
        lastIdx != null && questions[lastIdx]?.section_id === nextSection.id
          ? lastIdx
          : questions.findIndex(q => q.section_id === nextSection.id)
      if (targetIdx !== -1) setCurrentIndex(targetIdx)

      currentSectionIdRef.current = nextSection.id
      saveAnswers()

      toast({
        title: (
          <span className="flex items-center gap-1.5 font-semibold text-xs text-primary">
            <Layers className="h-3.5 w-3.5 shrink-0" />
            <span>Switched to {nextSection.name}</span>
          </span>
        ) as any,
        duration: 5000,
        className: 'h-8 sm:h-9 py-0 px-3.5 pr-8 border border-primary/40 bg-card/95 backdrop-blur-md shadow-xs rounded-full flex items-center gap-2 text-xs whitespace-nowrap',
      })
    } else {
      // All sections exhausted — auto-submit
      toast({
        title: (
          <span className="flex items-center gap-1.5 font-semibold text-xs text-primary">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            <span>All sections completed — Auto-submitting...</span>
          </span>
        ) as any,
        duration: 5000,
        className: 'h-8 sm:h-9 py-0 px-3.5 pr-8 border border-primary/40 bg-card/95 backdrop-blur-md shadow-xs rounded-full flex items-center gap-2 text-xs whitespace-nowrap',
      })
      await handleSubmit()
    }
  }, [attempt.id, attempt.test.sections, questions, handleSubmit, sectionStates, saveAnswers])

  // ─── Authoritative section-expiry watcher ────────────────────────────────
  // Watches sectionTimers directly — far more reliable than prop-based onExpire.
  // Fires handleSectionExpire the moment the CURRENT section's timer hits 0.
  // Also fires a 30-second warning toast once per section.
  const handledExpiriesRef = useRef<Set<number>>(new Set())
  const warned30Ref = useRef<Set<number>>(new Set())

  useEffect(() => {
    if (!isSectional || !currentSectionId) return
    const remSecs = sectionTimers[String(currentSectionId)] ?? Infinity
    const isLocked = Boolean(sectionStates[String(currentSectionId)]?.locked)
    const sectionName = attempt.test.sections?.find(s => s.id === currentSectionId)?.name ?? 'this section'

    // 30-second warning — fires exactly once per section
    if (remSecs <= 30 && remSecs > 0 && !warned30Ref.current.has(currentSectionId)) {
      warned30Ref.current.add(currentSectionId)
      toast({
        title: (
          <span className="flex items-center gap-1.5 font-medium text-xs text-foreground">
            <Clock className="h-3.5 w-3.5 shrink-0 text-tertiary" />
            <span><strong className="text-tertiary font-bold">30s remaining</strong> in {sectionName} — section will lock automatically</span>
          </span>
        ) as any,
        duration: 8000,
        className: 'h-8 sm:h-9 py-0 px-3.5 pr-8 border border-tertiary/50 bg-card/95 backdrop-blur-md shadow-xs rounded-full flex items-center gap-2 text-xs whitespace-nowrap',
      })
    }

    // Expiry / Lock watcher — triggers handleSectionExpire when current section is locked or 0s
    if ((remSecs <= 0 || isLocked) && !handledExpiriesRef.current.has(currentSectionId)) {
      handledExpiriesRef.current.add(currentSectionId)
      handleSectionExpire(currentSectionId)
    }
  }, [sectionTimers, currentSectionId, isSectional, handleSectionExpire, sectionStates, attempt.test.sections])
  // ─────────────────────────────────────────────────────────────────────────

  const getPaletteColor = useCallback((q: Question) => {
    const a = answers[q.id]
    if (!a?.visited) return 'bg-muted text-muted-foreground hover:bg-muted/80'
    const isAnswered = (a.selected_option_ids?.length ?? 0) > 0 || (a.numerical_response != null && !isNaN(a.numerical_response))
    if (a.marked_for_review && isAnswered) return 'bg-secondary text-secondary-fg font-bold'
    if (a.marked_for_review) return 'bg-secondary/15 text-secondary border border-secondary/30'
    if (isAnswered) return 'bg-primary text-primary-fg font-bold'
    return 'bg-tertiary/15 text-tertiary border border-tertiary/30'
  }, [answers])

  // Global timer expiry — 30s warning + auto-submit when non-sectional timer hits 0
  const globalExpiredRef = useRef(false)
  const globalWarned30Ref = useRef(false)
  useEffect(() => {
    if (isUntimed || isSectional || globalRemaining == null) return
    // 30-second warning
    if (globalRemaining <= 30 && globalRemaining > 0 && !globalWarned30Ref.current) {
      globalWarned30Ref.current = true
      toast({
        title: (
          <span className="flex items-center gap-1.5 font-medium text-xs text-foreground">
            <Clock className="h-3.5 w-3.5 shrink-0 text-tertiary" />
            <span><strong className="text-tertiary font-bold">30s remaining</strong> — test will submit automatically</span>
          </span>
        ) as any,
        duration: 8000,
        className: 'h-8 sm:h-9 py-0 px-3.5 pr-8 border border-tertiary/50 bg-card/95 backdrop-blur-md shadow-xs rounded-full flex items-center gap-2 text-xs whitespace-nowrap',
      })
    }
    // Expiry — auto-submit
    if (globalRemaining <= 0 && !globalExpiredRef.current) {
      globalExpiredRef.current = true
      handleSubmit()
    }
  }, [globalRemaining, isSectional, isUntimed, handleSubmit])

  // Ensure window/body does not scroll during test attempt
  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalBodyOverflow = document.body.style.overflow
    const originalHtmlHeight = document.documentElement.style.height
    const originalBodyHeight = document.body.style.height

    document.documentElement.style.overflow = 'hidden'
    document.documentElement.style.height = '100%'
    document.body.style.overflow = 'hidden'
    document.body.style.height = '100%'

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow
      document.documentElement.style.height = originalHtmlHeight
      document.body.style.overflow = originalBodyOverflow
      document.body.style.height = originalBodyHeight
    }
  }, [])

  const markedCount = questions.filter((q) => answers[q.id]?.marked_for_review).length
  const visitedCount = questions.filter((q) => answers[q.id]?.visited).length
  const notVisitedCount = questions.length - visitedCount
  const unansweredCount = visitedCount - answeredCount

  // Derive locked section IDs for palette + answer blocking
  const lockedSectionIds = new Set(
    Object.entries(sectionStates)
      .filter(([, v]) => v?.locked === true)
      .map(([k]) => Number(k))
  )
  const currentSectionLocked = currentQuestion?.section_id != null && lockedSectionIds.has(currentQuestion.section_id)

  // Check if there is any accessible (unlocked) question before currentIndex
  const hasPrevUnlocked = (() => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      const q = questions[i]
      if (q?.section_id == null || !lockedSectionIds.has(q.section_id)) return true
    }
    return false
  })()

  // Check if there is any accessible (unlocked) question after currentIndex
  const hasNextUnlocked = (() => {
    for (let i = currentIndex + 1; i < questions.length; i++) {
      const q = questions[i]
      if (q?.section_id == null || !lockedSectionIds.has(q.section_id)) return true
    }
    return false
  })()

  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : null
  const hasSelectedAnswer = (currentAnswer?.selected_option_ids?.length ?? 0) > 0 || (currentAnswer?.numerical_response != null && !isNaN(currentAnswer.numerical_response))

  const goToPrevQuestion = useCallback(() => {
    let prev = currentIndex - 1
    while (prev >= 0 && questions[prev]?.section_id != null && lockedSectionIds.has(questions[prev].section_id!)) prev--
    if (prev >= 0) setCurrentIndex(prev)
  }, [currentIndex, questions, lockedSectionIds])

  const goToNextQuestion = useCallback(() => {
    let next = currentIndex + 1
    while (next < questions.length && questions[next]?.section_id != null && lockedSectionIds.has(questions[next].section_id!)) next++
    if (next < questions.length) setCurrentIndex(next)
  }, [currentIndex, questions, lockedSectionIds])

  const effectiveCurrentSection = attempt.test.sections?.find(s => s.id === currentQuestion?.section_id) || currentSection || attempt.test.sections?.[0]

  const activePaletteSection = (drawerSectionId != null && attempt.test.sections)
    ? (attempt.test.sections.find(s => s.id === drawerSectionId) || effectiveCurrentSection)
    : effectiveCurrentSection

  const mobilePaletteQuestions = activePaletteSection && attempt.test.sections && attempt.test.sections.length > 0 && questions.some(q => q.section_id != null)
    ? questions.filter(q => q.section_id === activePaletteSection.id)
    : questions

  const mobileRemainingTime = isUntimed
    ? 'Untimed'
    : isSectional && currentSectionId
    ? formatTime(sectionTimers[String(currentSectionId)] ?? ((effectiveCurrentSection?.duration_minutes || 0) * 60))
    : formatTime(globalRemaining || 0)

  // Renders 5-column palette items with unvisited ranges collapsed (e.g. 16 - 23 not visited) matching Mockup 1
  const renderMobilePaletteItems = () => {
    const secQuestions = mobilePaletteQuestions
    const items: React.ReactNode[] = []
    let i = 0

    while (i < secQuestions.length) {
      const q = secQuestions[i]
      const idx = questions.indexOf(q)
      const isVisited = Boolean(answers[q.id]?.visited)

      // Check for consecutive unvisited questions of length >= 4
      if (!isVisited) {
        let runEnd = i
        while (runEnd < secQuestions.length && !answers[secQuestions[runEnd].id]?.visited) {
          runEnd++
        }
        const runLength = runEnd - i
        const rangeKey = `unvisited-${activePaletteSection?.id ?? 'all'}-${i}-${runEnd - 1}`
        const hasSelectedInRange = selectedPaletteIndex >= idx && selectedPaletteIndex < idx + runLength

        if (runLength >= 4 && !expandedUnvisitedRanges.has(rangeKey) && !hasSelectedInRange) {
          const firstQInRun = secQuestions[i]
          const firstQIdx = questions.indexOf(firstQInRun)
          const lastQInRun = secQuestions[runEnd - 1]
          const lastQIdx = questions.indexOf(lastQInRun)
          const startQNum = firstQIdx !== -1 ? firstQIdx + 1 : i + 1
          const endQNum = lastQIdx !== -1 ? lastQIdx + 1 : runEnd
          const capturedRangeKey = rangeKey
          const capturedTargetIdx = firstQIdx !== -1 ? firstQIdx : 0

          items.push(
            <button
              key={rangeKey}
              type="button"
              onClick={() => {
                if (capturedTargetIdx >= 0) {
                  setSelectedPaletteIndex(capturedTargetIdx)
                }
                setExpandedUnvisitedRanges(prev => new Set(prev).add(capturedRangeKey))
              }}
              className="col-span-5 h-12 sm:h-14 rounded-xl border border-border/80 bg-card hover:bg-muted/30 text-xs sm:text-sm font-medium text-muted-foreground/80 flex items-center justify-center transition-colors shadow-2xs"
            >
              {startQNum} - {endQNum} not visited
            </button>
          )
          i = runEnd
          continue
        }
      }

      // Single question button
      const isCurrent = idx === selectedPaletteIndex
      const capturedIdx = idx
      const a = answers[q.id]
      const isAnswered = (a?.selected_option_ids?.length ?? 0) > 0 || (a?.numerical_response != null && !isNaN(a?.numerical_response))
      const isMarked = Boolean(a?.marked_for_review)
      const isVis = Boolean(a?.visited)

      let tileClass = 'bg-card text-muted-foreground/70 border border-border/70'
      if (isMarked) {
        tileClass = 'border-2 border-destructive text-destructive font-bold bg-card'
      } else if (isAnswered) {
        tileClass = 'bg-tertiary/15 text-tertiary font-bold border border-tertiary/30'
      } else if (isVis) {
        tileClass = 'bg-muted/40 text-foreground border border-border'
      }

      const selectedOutline = isCurrent
        ? isMarked
          ? 'ring-2 ring-destructive border-2 border-destructive shadow-xs'
          : 'ring-2 ring-primary border-2 border-primary shadow-xs'
        : 'hover:opacity-90'

      items.push(
        <button
          key={q.id}
          type="button"
          onClick={() => {
            if (capturedIdx >= 0) {
              if (selectedPaletteIndex === capturedIdx) {
                setCurrentIndex(capturedIdx)
                setPaletteDrawerOpen(false)
              } else {
                setSelectedPaletteIndex(capturedIdx)
              }
            }
          }}
          className={`h-12 sm:h-14 w-full rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-center cursor-pointer shadow-2xs ${tileClass} ${selectedOutline}`}
        >
          {idx + 1}
        </button>
      )
      i++
    }

    return items
  }

  if (!currentQuestion && questions.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-muted-foreground">No questions found in this test.</p>
      </div>
    )
  }

  return (
    <div className="app-shell-root flex h-screen h-[100dvh] max-h-screen max-h-[100dvh] flex-col bg-background select-none overflow-hidden">
      {/* Responsive Top Test Navigation Bar */}
      <header className="border-b border-border bg-card shrink-0 shadow-2xs">
        {/* Mobile Top Bar (< lg): Grip Palette Button | Centered Timer Badge | Submit Button */}
        <div className="lg:hidden w-full px-3 py-2 flex items-center justify-between gap-2">
          {/* Left: 6-dots Grip Palette button */}
          <button
            type="button"
            onClick={() => {
              setSelectedPaletteIndex(safeCurrentIndex)
              setDrawerSectionId(currentQuestion?.section_id || (attempt.test.sections && attempt.test.sections[0]?.id) || null)
              setPaletteDrawerOpen(true)
            }}
            className="h-9 w-9 flex items-center justify-center rounded-xl border border-border bg-card hover:bg-muted/50 text-foreground transition-colors shadow-2xs"
            aria-label="Open Question Palette"
          >
            <GripVertical className="h-5 w-5 text-foreground" />
          </button>

          {/* Center: Warm Timer Badge (only for timed tests) */}
          {!isUntimed && (
            <div className="flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-tertiary/10 border border-tertiary/30 text-tertiary font-mono text-xs sm:text-sm font-bold shadow-2xs">
              <Clock className="h-3.5 w-3.5 text-tertiary" />
              <span>{mobileRemainingTime}</span>
            </div>
          )}

          {/* Right: Submit Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSubmitDialogOpen(true)}
            className="h-8 px-4 rounded-xl border-primary/40 text-primary hover:bg-primary/10 font-bold text-xs shadow-2xs"
          >
            Submit
          </Button>
        </div>

        {/* Mobile Slim Progress Line */}
        <div className="lg:hidden w-full bg-muted h-1 overflow-hidden">
          <div
            className="bg-primary h-1 transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
          />
        </div>

        {/* Desktop Top Bar (>= lg) */}
        <div className="hidden lg:flex w-full px-6 lg:px-8 py-2.5 items-center justify-between gap-4">
          {/* Desktop Left: PrepKiya Logo + Test Title */}
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/logo.svg"
              alt="PrepKiya Logo"
              className="h-9 w-9 rounded-lg object-cover shrink-0 shadow-xs border border-border/50"
            />
            <div className="min-w-0">
              <h1 className="font-bold text-base leading-tight truncate text-foreground">{attempt.test.title}</h1>
              {attempt.test.language && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                  <span className="uppercase font-semibold text-[10px] text-muted-foreground">{attempt.test.language}</span>
                </div>
              )}
            </div>
          </div>

          {/* Calculator toggle if enabled */}
          {attempt.test.exam_config?.calculator_enabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCalculator(prev => !prev)}
              className="h-8 px-2.5 text-xs font-bold gap-1 rounded-lg border-border"
              title="Toggle Calculator"
            >
              <CalcIcon className="h-4 w-4 text-primary" />
              <span className="hidden md:inline">Calculator</span>
            </Button>
          )}

          {/* Test Instructions Toolbar Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowInstructionsModal(true)}
            className="h-8 px-2.5 text-xs font-bold gap-1.5 rounded-lg border-border"
            title="View Test Instructions & Rules"
          >
            <FileText className="h-4 w-4 text-primary" />
            <span className="hidden sm:inline">Instructions</span>
          </Button>

          {/* Desktop Timer Display (hidden for untimed tests) */}
          {!isUntimed && (
            <div className="flex items-center justify-center gap-2 flex-wrap ml-auto">
              {isSectional && attempt.test.sections && attempt.test.sections.length > 1 ? (
                attempt.test.sections.map((sec) => {
                  const isCurrent = currentSectionId === sec.id
                  const isLocked = sectionStates[String(sec.id)]?.locked === true
                  const remSecs = sectionTimers[String(sec.id)] ?? ((sec.duration_minutes || 0) * 60)
                  return (
                    <TimerDisplay
                      key={sec.id}
                      remainingSeconds={isLocked ? 0 : remSecs}
                      sectionName={sec.name}
                      isSectional={true}
                      isActiveSection={isCurrent && !isLocked}
                    />
                  )
                })
              ) : (
                <TimerDisplay
                  remainingSeconds={
                    isSectional && currentSectionId
                      ? (sectionTimers[String(currentSectionId)] ?? ((currentSection?.duration_minutes || 0) * 60))
                      : (globalRemaining || 0)
                  }
                  sectionName={isSectional ? currentSection?.name : null}
                  isSectional={isSectional}
                  isActiveSection={true}
                />
              )}
            </div>
          )}

          {/* Right: Submit Button */}
          <Button
            size="sm"
            variant="outline"
            className="border-primary/40 text-primary hover:bg-primary/10 font-bold gap-1.5 h-8 sm:h-9 text-xs sm:text-sm shrink-0"
            onClick={() => setSubmitDialogOpen(true)}
          >
            <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>Submit Test</span>
          </Button>
        </div>

        {/* Section Tabs Bar if multiple sections present */}
        {attempt.test.sections && attempt.test.sections.length > 1 && (
          <div className="w-full px-3 sm:px-6 lg:px-8 py-1.5 bg-muted/40 border-t border-border flex items-center gap-2 overflow-x-auto">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0 hidden sm:inline flex items-center gap-1">
              <Layers className="h-3 w-3" /> Sections:
            </span>
            {attempt.test.sections.map((sec) => {
              const secQuestions = questions.filter(q => q.section_id === sec.id)
              const secAnswered = secQuestions.filter(q => {
                const a = answers[q.id]
                return (a?.selected_option_ids?.length ?? 0) > 0 || (a?.numerical_response != null && !isNaN(a.numerical_response))
              }).length
              const isLocked = sectionStates[String(sec.id)]?.locked === true
              const isCurrent = currentQuestion.section_id === sec.id
              const secRemSeconds = sectionTimers[String(sec.id)] ?? ((sec.duration_minutes || 0) * 60)

              return (
                <button
                  key={sec.id}
                  ref={(el) => { sectionTabRefs.current[String(sec.id)] = el }}
                  type="button"
                  disabled={isLocked}
                  onClick={() => {
                    if (isLocked) return
                    // Resume last-visited question in this section, or fall back to first question
                    const lastIdx = sectionLastIndexRef.current[String(sec.id)]
                    if (lastIdx != null && questions[lastIdx]?.section_id === sec.id) {
                      setCurrentIndex(lastIdx)
                    } else {
                      const firstIdx = questions.findIndex(q => q.section_id === sec.id)
                      if (firstIdx !== -1) setCurrentIndex(firstIdx)
                    }
                  }}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shrink-0 ${
                    isCurrent
                      ? 'bg-primary text-primary-fg shadow-2xs'
                      : isLocked
                      ? 'bg-muted/50 text-muted-foreground opacity-60 cursor-not-allowed'
                      : 'bg-card text-foreground hover:bg-muted/60 border border-border/70'
                  }`}
                >
                  {isLocked && <Lock className="h-3 w-3 text-destructive" />}
                  <span>{sec.name}</span>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 bg-background/50 text-current border-current/30">
                    {sec.max_attempts && sec.max_attempts > 0 && sec.max_attempts < sec.total_questions
                      ? `${secAnswered}/${sec.max_attempts} max`
                      : `${secAnswered}/${sec.total_questions}`}
                  </Badge>
                  {isSectional && (
                    <span className="text-[10px] font-mono opacity-90 ml-0.5 flex items-center gap-0.5">
                      <Clock className="h-2.5 w-2.5" />
                      {formatTime(secRemSeconds)}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </header>

      {/* Floating Calculator Overlay */}
      {showCalculator && (
        <div className="fixed bottom-6 right-6 z-50 shadow-glass-lg animate-in fade-in zoom-in-95">
          <CalculatorWidget onClose={() => setShowCalculator(false)} />
        </div>
      )}

      {/* Main Container */}
      <div className="flex flex-1 overflow-hidden min-w-0">
        {/* Responsive Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Middle Split: Optional Passage / Context Area + Question Area */}
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-w-0">
            <aside
              className={`transition-all duration-300 ease-in-out overflow-y-auto overflow-x-hidden bg-card/30 shrink-0 ${
                hasContext
                  ? 'hidden lg:block w-full lg:w-[46%] xl:w-[48%] opacity-100 p-5 lg:p-6 border-r border-border'
                  : 'w-0 max-w-0 opacity-0 p-0 border-0 pointer-events-none overflow-hidden'
              }`}
              aria-hidden={!hasContext}
            >
              {displayedContext && (
                <div className="w-full min-w-[280px] sm:min-w-[380px] md:min-w-[320px] lg:min-w-[380px] transition-opacity duration-300">
                  <QuestionContextCard
                    context={displayedContext}
                    format={currentQuestion.render_format}
                  />
                </div>
              )}
            </aside>

            {/* Question & Options Area */}
            <main className="flex-1 overflow-y-auto p-3 sm:p-6 md:p-8 pb-36 sm:pb-44 lg:pb-8 min-w-0 transition-all duration-300 ease-in-out">
              <div className={`mx-auto space-y-4 sm:space-y-6 transition-all duration-300 ease-in-out ${hasContext ? 'max-w-xl xl:max-w-2xl' : 'max-w-3xl'}`}>
                
                {/* Desktop Header / Meta (>= lg) */}
                <div className="hidden lg:block space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-foreground">Question {currentIndex + 1} of {questions.length}</span>
                      <Badge variant="secondary" className="text-[10px] sm:text-xs font-mono uppercase">{currentQuestion.question_type}</Badge>
                      {isTestAttemptCapped && attempt.test.max_attempts && (
                        <Badge variant="outline" className="text-[10px] sm:text-xs font-semibold border-primary/30 bg-primary/10 text-primary">
                          Attempt any {attempt.test.max_attempts}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold">
                      <span className="text-primary bg-primary/10 px-2 py-0.5 rounded border border-primary/20">
                        +{currentQuestion.positive_marks} Marks
                      </span>
                      {currentQuestion.negative_marks > 0 && (
                        <span className="text-destructive bg-destructive/10 px-2 py-0.5 rounded border border-destructive/20">
                          -{currentQuestion.negative_marks} Neg
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Multi-correct helper notice */}
                  {currentQuestion.question_type === 'MULTI_CORRECT' && (
                    <div className="text-[11px] text-primary font-bold bg-primary/10 border border-primary/20 px-3 py-1 rounded-lg flex items-center gap-1.5">
                      <span>☑️ Multiple Correct Options. Select all that apply.</span>
                    </div>
                  )}

                  <Progress value={((currentIndex + 1) / questions.length) * 100} className="h-1.5 rounded-full" />
                </div>

              {/* Mobile Subheader & Meta (< lg) */}
              {hasPassageContext ? (
                /* Mockup 3: Reading Comprehension / Passage View */
                <div className="lg:hidden space-y-3">
                  <div className="flex items-center justify-between text-xs font-semibold text-foreground">
                    <span>{formatContextType(displayedContext?.context_type)} · Q {currentIndex + 1} of {questions.length}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground bg-muted px-2 py-0.5 rounded-md font-semibold text-xs">+{currentQuestion.positive_marks}</span>
                      {currentQuestion.negative_marks > 0 && (
                        <span className="text-destructive bg-destructive/10 px-2 py-0.5 rounded-md font-semibold text-xs">-{currentQuestion.negative_marks}</span>
                      )}
                    </div>
                  </div>

                  {/* Segmented Switcher: [ Passage ] and [ Question ] */}
                  <div className="grid grid-cols-2 gap-2 p-0.5">
                    <button
                      type="button"
                      onClick={() => setMobileRcTab('passage')}
                      className={`py-2 px-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                        mobileRcTab === 'passage'
                          ? 'border-primary bg-primary/10 text-primary shadow-xs'
                          : 'border-border bg-card text-foreground hover:bg-muted/40'
                      }`}
                    >
                      <BookOpen className="h-3.5 w-3.5" />
                      <span>Passage</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMobileRcTab('question')}
                      className={`py-2 px-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                        mobileRcTab === 'question'
                          ? 'border-primary bg-primary/10 text-primary shadow-xs'
                          : 'border-border bg-card text-foreground hover:bg-muted/40'
                      }`}
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                      <span>Question</span>
                    </button>
                  </div>

                  {/* Teaser Preview Card when on Question Tab */}
                  {mobileRcTab === 'question' && (
                    <div className="rounded-xl border border-border/70 bg-muted/30 p-3 flex items-center justify-between gap-2 shadow-2xs">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <BookOpen className="h-4 w-4 text-primary shrink-0" />
                        <span className="text-xs text-muted-foreground truncate italic">
                          {passageSnippet ? `"${passageSnippet}"` : 'Refer to passage / stimulus'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setMobileRcTab('passage')}
                        className="text-xs font-semibold text-primary hover:underline shrink-0"
                      >
                        Read full passage
                      </button>
                    </div>
                  )}

                  {/* Directions accordion on question tab if directions exist */}
                  {mobileRcTab === 'question' && displayedContext?.direction_text && (
                    <div className="rounded-xl border border-border/70 bg-card overflow-hidden shadow-2xs">
                      <button
                        type="button"
                        onClick={() => setDirectionsOpen(prev => !prev)}
                        className="w-full px-3.5 py-2 flex items-center justify-between gap-2 text-xs font-semibold text-muted-foreground hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Compass className="h-3.5 w-3.5 text-primary" />
                          <span>Directions</span>
                        </div>
                        {directionsOpen ? (
                          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </button>
                      {directionsOpen && (
                        <div className="px-3.5 pb-2.5 pt-1 text-xs text-muted-foreground leading-relaxed border-t border-border/50 italic">
                          <KatexRenderer text={displayedContext.direction_text} format={currentQuestion.render_format} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : hasDirectionsOnly ? (
                /* Mockup 2: Standard Question with Collapsible Directions Accordion */
                <div className="lg:hidden space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-foreground">
                      {effectiveCurrentSection?.name ? <span className="text-primary font-semibold mr-1">{effectiveCurrentSection.name} ·</span> : null}
                      Q {safeCurrentIndex + 1} of {questions.length}
                    </span>
                    <div className="flex items-center gap-1.5 text-xs font-semibold">
                      <span className="text-muted-foreground bg-muted px-2 py-0.5 rounded-md font-semibold">+{currentQuestion.positive_marks}</span>
                      {currentQuestion.negative_marks > 0 && (
                        <span className="text-destructive bg-destructive/10 px-2 py-0.5 rounded-md font-semibold">-{currentQuestion.negative_marks}</span>
                      )}
                    </div>
                  </div>

                  {/* Collapsible Directions Accordion */}
                  <div className="rounded-xl border border-border/70 bg-card overflow-hidden shadow-2xs">
                    <button
                      type="button"
                      onClick={() => setDirectionsOpen(prev => !prev)}
                      className="w-full px-3.5 py-2.5 flex items-center justify-between gap-2 text-xs font-semibold text-foreground hover:bg-muted/40 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Compass className="h-4 w-4 text-muted-foreground" />
                        <span>Directions</span>
                      </div>
                      {directionsOpen ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                    {directionsOpen && (
                      <div className="px-3.5 pb-3 pt-1 text-xs text-muted-foreground leading-relaxed border-t border-border/50">
                        <KatexRenderer text={effectiveDirectionText} format={currentQuestion.render_format} />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Standard Question without Context */
                <div className="lg:hidden flex items-center justify-between pb-1">
                  <span className="text-sm font-bold text-foreground">
                    {effectiveCurrentSection?.name ? <span className="text-primary font-semibold mr-1">{effectiveCurrentSection.name} ·</span> : null}
                    Q {safeCurrentIndex + 1} of {questions.length}
                  </span>
                  <div className="flex items-center gap-1.5 text-xs font-semibold">
                    <span className="text-muted-foreground bg-muted px-2 py-0.5 rounded-md font-semibold">+{currentQuestion.positive_marks}</span>
                    {currentQuestion.negative_marks > 0 && (
                      <span className="text-destructive bg-destructive/10 px-2 py-0.5 rounded-md font-semibold">-{currentQuestion.negative_marks}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Mobile Multi-correct helper notice */}
              {currentQuestion.question_type === 'MULTI_CORRECT' && (
                <div className="lg:hidden text-[11px] text-primary font-bold bg-primary/10 border border-primary/20 px-3 py-1 rounded-lg flex items-center gap-1.5">
                  <span>☑️ Multiple Correct Options. Select all that apply.</span>
                </div>
              )}

              {/* Passage Content View on Mobile (when mobileRcTab === 'passage') */}
              {mobileRcTab === 'passage' && hasPassageContext && displayedContext ? (
                <div className="lg:hidden rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4 shadow-2xs">
                  <div className="flex items-center justify-between border-b border-border/50 pb-2.5">
                    <h3 className="text-sm font-bold text-foreground">{cleanContextTitle(displayedContext.title)}</h3>
                    <Badge variant="outline" className="text-[10px] font-semibold bg-primary/10 text-primary border-primary/20">
                      {formatContextType(displayedContext.context_type)}
                    </Badge>
                  </div>
                  {displayedContext.direction_text && (
                    <div className="text-xs text-muted-foreground italic bg-muted/20 p-2.5 rounded-xl border border-border/40">
                      <KatexRenderer text={displayedContext.direction_text} format={currentQuestion.render_format} />
                    </div>
                  )}
                  {(displayedContext.context_image_url || displayedContext.media_url) && (
                    <div className="p-3 rounded-xl border border-border/70 bg-muted/20 flex flex-col items-center">
                      <img
                        src={displayedContext.context_image_url || displayedContext.media_url!}
                        alt={displayedContext.title || 'Context diagram'}
                        className="max-h-72 sm:max-h-80 w-auto max-w-full object-contain rounded-lg border border-border/50"
                      />
                    </div>
                  )}
                  {displayedContext.body_text && (
                    <div className="text-xs sm:text-sm leading-relaxed text-foreground space-y-3 font-normal">
                      <KatexRenderer text={displayedContext.body_text} format={currentQuestion.render_format} />
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMobileRcTab('question')}
                    className="w-full mt-3 border-primary/40 text-primary font-bold text-xs"
                  >
                    Go to Question →
                  </Button>
                </div>
              ) : (
                <>
                  {/* Optional Section Attempt Limit Indicator — strictly shown ONLY when section has an optional attempt cap (e.g. JEE) */}
                  {isSectionAttemptCapped && currentSection && (
                    <div className="flex items-center justify-between px-3.5 py-2 rounded-xl bg-muted/30 border border-border/70 text-xs">
                      <span className="font-semibold text-foreground">Section Attempt Limit:</span>
                      <span className="text-muted-foreground font-medium">
                        Attempted <strong className={sectionAnsweredCount >= currentSection.max_attempts! ? 'text-primary font-bold' : 'text-foreground'}>{sectionAnsweredCount}</strong> of <strong>{currentSection.max_attempts}</strong> allowed
                      </span>
                    </div>
                  )}

                  {/* Test-level Attempt Limit Indicator — strictly shown ONLY when test has a global attempt cap */}
                  {isTestAttemptCapped && !isSectionAttemptCapped && attempt.test.max_attempts && (
                    <div className="flex items-center justify-between px-3.5 py-2 rounded-xl bg-muted/30 border border-border/70 text-xs">
                      <span className="font-semibold text-foreground">Test Attempt Limit:</span>
                      <span className="text-muted-foreground font-medium">
                        Attempted <strong className={answeredCount >= attempt.test.max_attempts ? 'text-primary font-bold' : 'text-foreground'}>{answeredCount}</strong> of <strong>{attempt.test.max_attempts}</strong> allowed
                      </span>
                    </div>
                  )}

                  {/* Question Text Card */}
                  <div className="rounded-xl border border-border bg-card p-4 sm:p-6 shadow-2xs space-y-4 overflow-x-auto">
                    <KatexRenderer
                      text={effectiveQuestionText}
                      format={currentQuestion.render_format}
                      highlightOptions={currentQuestion.options.map(o => ({ text: o.text || undefined }))}
                      className="text-sm sm:text-base leading-relaxed font-medium text-foreground"
                    />

                    {/* Question Figure / Diagram */}
                    {currentQuestion.figure_image_url && (
                      <div className="p-3 rounded-xl border border-border/70 bg-muted/20 flex flex-col items-center">
                        <img
                          src={currentQuestion.figure_image_url}
                          alt="Question diagram"
                          className="max-h-72 sm:max-h-80 w-auto max-w-full object-contain rounded-lg border border-border/50"
                        />
                      </div>
                    )}

                    {/* Question Text Image */}
                    {currentQuestion.question_image_url && (
                      <div className="p-3 rounded-xl border border-border/70 bg-muted/20 flex flex-col items-center">
                        <img
                          src={currentQuestion.question_image_url}
                          alt="Question image"
                          className="max-h-72 sm:max-h-80 w-auto max-w-full object-contain rounded-lg border border-border/50"
                        />
                      </div>
                    )}
                  </div>

                  {/* MCQ & MULTI_CORRECT Options (Mockup 2 & Mockup 3) */}
                  {currentQuestion.question_type !== 'NUMERICAL' && (
                    <div className="space-y-2.5">
                      {currentSectionLocked && (
                        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-xs text-destructive font-medium">
                          <Lock className="h-3.5 w-3.5 shrink-0" />
                          <span>Section locked — answers cannot be changed after time expires.</span>
                        </div>
                      )}
                      {currentQuestion.options.map((opt) => {
                        const isSelected = answers[currentQuestion.id]?.selected_option_ids?.includes(opt.id)
                        const isMulti = currentQuestion.question_type === 'MULTI_CORRECT'

                        return (
                          <button
                            key={opt.id}
                            type="button"
                            disabled={currentSectionLocked}
                            onClick={() => !currentSectionLocked && handleSelectOption(opt.id)}
                            className={`w-full flex items-center gap-3 rounded-2xl border p-3.5 sm:p-4 text-left transition-all ${
                              currentSectionLocked
                                ? 'cursor-not-allowed opacity-60 pointer-events-none'
                                : 'cursor-pointer'
                            } ${
                              isSelected
                                ? 'border-primary bg-primary/5 ring-1 ring-primary/25 shadow-2xs'
                                : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
                            }`}
                          >
                            <span className={`flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center ${isMulti ? 'rounded-md' : 'rounded-full'} text-xs font-bold transition-colors ${
                              isSelected
                                ? 'bg-primary text-primary-fg shadow-2xs'
                                : 'border border-border/90 text-muted-foreground bg-muted/20'
                            }`}>
                              {isSelected ? (
                                <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                              ) : (
                                opt.label
                              )}
                            </span>
                            <div className="flex-1 text-xs sm:text-sm leading-relaxed overflow-x-auto text-foreground space-y-2">
                              {opt.text && <KatexRenderer text={opt.text} format={currentQuestion.render_format} />}
                              {opt.option_image_url && (
                                <img
                                  src={opt.option_image_url}
                                  alt={`Option ${opt.label}`}
                                  className="max-h-36 sm:max-h-44 w-auto max-w-full object-contain rounded-lg border border-border/50 bg-background/50 p-1"
                                />
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}

                  {/* Numerical Answer Input */}
                  {currentQuestion.question_type === 'NUMERICAL' && (
                    <div className="rounded-xl border border-border bg-card p-4 sm:p-6 space-y-2">
                      {currentSectionLocked && (
                        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-xs text-destructive font-medium mb-3">
                          <Lock className="h-3.5 w-3.5 shrink-0" />
                          <span>Section locked — answers cannot be changed after time expires.</span>
                        </div>
                      )}
                      <label className="text-xs sm:text-sm font-semibold block text-foreground">Enter Numerical Response:</label>
                      <input
                        type="number"
                        step="any"
                        disabled={currentSectionLocked}
                        value={answers[currentQuestion.id]?.numerical_response ?? ''}
                        onChange={(e) => {
                          if (currentSectionLocked) return
                          if (e.target.value !== '' && !isQuestionAnswered(currentQuestion.id) && !canAnswerCurrentQuestion()) return
                          updateAnswer(currentQuestion.id, {
                            numerical_response: e.target.value !== '' ? Number(e.target.value) : null
                          })
                        }}
                        className={`flex h-11 w-full max-w-xs rounded-lg border border-input bg-background text-foreground px-3 py-2 text-base shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                          currentSectionLocked ? 'opacity-60 cursor-not-allowed bg-muted/30' : ''
                        }`}
                        placeholder="e.g. 14.5"
                      />
                    </div>
                  )}

                  {/* Mobile Clearance Spacer to ensure last option is fully visible above sticky bottom bar */}
                  <div className="h-16 lg:hidden shrink-0" aria-hidden="true" />
                </>
              )}
              </div>
            </main>
          </div>

          {/* Fixed Desktop Navigation and Action Bar (>= lg) - Spans full width under passage and question */}
          <div className="hidden lg:flex items-center justify-between px-6 lg:px-8 py-3 border-t border-border bg-card/95 backdrop-blur-xs shrink-0 z-10 shadow-xs">
            {/* Left Group: Mark for Review & Clear Response */}
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant={answers[currentQuestion.id]?.marked_for_review ? 'secondary' : 'outline'}
                size="default"
                disabled={currentSectionLocked}
                onClick={() => !currentSectionLocked && handleToggleReview()}
                className={`gap-2 text-xs sm:text-sm font-semibold h-10 px-4 transition-all ${
                  currentSectionLocked
                    ? 'opacity-50 cursor-not-allowed border-border'
                    : answers[currentQuestion.id]?.marked_for_review
                    ? 'bg-secondary/15 text-secondary border-secondary/40 font-bold'
                    : 'border-border text-foreground hover:bg-muted/50'
                }`}
              >
                <BookmarkCheck className={`h-4 w-4 ${answers[currentQuestion.id]?.marked_for_review ? 'text-secondary fill-secondary/20' : 'text-muted-foreground'}`} />
                <span>{answers[currentQuestion.id]?.marked_for_review ? 'Marked for Review' : 'Mark for Review'}</span>
              </Button>

              <Button
                type="button"
                variant="outline"
                size="default"
                disabled={currentSectionLocked}
                onClick={() => {
                  if (currentSectionLocked) return
                  if (hasSelectedAnswer) {
                    handleClearResponse()
                  } else {
                    toast({
                      title: 'No response to clear on this question',
                      description: 'To free up an attempt slot, go to a previously answered question and click "Clear Response".',
                      duration: 4000,
                    })
                  }
                }}
                className={`gap-2 text-xs sm:text-sm font-semibold h-10 px-4 transition-all ${
                  currentSectionLocked
                    ? 'opacity-40 cursor-not-allowed border-border text-muted-foreground'
                    : hasSelectedAnswer
                    ? 'border-destructive/30 text-destructive hover:bg-destructive/10 hover:border-destructive/50'
                    : 'border-border text-muted-foreground/70 hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <RotateCcw className="h-4 w-4" />
                <span>Clear Response</span>
              </Button>
            </div>

            {/* Right Group: Previous & Next / Submit Test */}
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="default"
                disabled={!hasPrevUnlocked}
                onClick={goToPrevQuestion}
                className="gap-2 text-xs sm:text-sm font-bold border-border px-5 h-10 min-w-[105px] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted/50"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Previous</span>
              </Button>

              {safeCurrentIndex === questions.length - 1 ? (
                <Button
                  type="button"
                  size="default"
                  onClick={() => setSubmitDialogOpen(true)}
                  className="gap-2 text-xs sm:text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 px-6 h-10 min-w-[130px] shadow-xs"
                >
                  <span>Submit Test</span>
                  <Send className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  type="button"
                  size="default"
                  disabled={!hasNextUnlocked}
                  onClick={goToNextQuestion}
                  className="gap-2 text-xs sm:text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 px-6 h-10 min-w-[105px] disabled:opacity-40 disabled:cursor-not-allowed shadow-xs"
                >
                  <span>Next</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Sidebar Question Palette */}
        <aside className="hidden lg:flex w-72 border-l border-border bg-card flex-col shrink-0 overflow-hidden">
          <div className="p-4 border-b border-border shrink-0">
            <h2 className="font-bold text-sm text-foreground">Question Palette</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Click any number to jump to question</p>
          </div>

          <div className="p-4 flex-1 overflow-hidden min-h-0">
            <PaletteGrid
              questions={questions}
              sections={attempt.test.sections}
              currentIndex={currentIndex}
              answers={answers}
              answeredCount={answeredCount}
              unansweredCount={unansweredCount}
              markedCount={markedCount}
              notVisitedCount={notVisitedCount}
              getPaletteColor={getPaletteColor}
              onSelectQuestion={(idx) => setCurrentIndex(idx)}
              lockedSectionIds={lockedSectionIds}
              isMobile={false}
              maxAttempts={isTestAttemptCapped ? attempt.test.max_attempts : null}
            />
          </div>
        </aside>
      </div>

      {/* Mobile Sticky Bottom Navigation Bar (< lg): Previous | Mark | Clear | Next */}
      <div className="fixed bottom-0 left-0 right-0 z-30 lg:hidden border-t border-border bg-card/95 backdrop-blur-md grid grid-cols-4 divide-x divide-border pb-[env(safe-area-inset-bottom,0px)]">
        {/* Previous */}
        <button
          type="button"
          disabled={!hasPrevUnlocked}
          onClick={goToPrevQuestion}
          className="flex flex-col items-center justify-center py-2.5 px-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mb-0.5" />
          <span className="text-[11px] font-medium">Previous</span>
        </button>

        {/* Mark for Review */}
        <button
          type="button"
          disabled={currentSectionLocked}
          onClick={handleToggleReview}
          className={`flex flex-col items-center justify-center py-2.5 px-1 transition-colors ${
            answers[currentQuestion?.id]?.marked_for_review
              ? 'text-secondary font-semibold'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Bookmark className={`h-4 w-4 mb-0.5 ${answers[currentQuestion?.id]?.marked_for_review ? 'fill-secondary text-secondary' : ''}`} />
          <span className="text-[11px] font-medium">{answers[currentQuestion?.id]?.marked_for_review ? 'Marked' : 'Mark'}</span>
        </button>

        {/* Clear Response */}
        <button
          type="button"
          disabled={currentSectionLocked}
          onClick={() => {
            if (currentSectionLocked) return
            if (hasSelectedAnswer) {
              handleClearResponse()
            } else {
              toast({
                title: 'No response to clear on this question',
                description: 'To free up an attempt slot, go to a previously answered question and tap "Clear".',
                duration: 4500,
              })
            }
          }}
          className={`flex flex-col items-center justify-center py-2.5 px-1 transition-colors ${
            currentSectionLocked
              ? 'opacity-30 cursor-not-allowed text-muted-foreground'
              : hasSelectedAnswer
              ? 'text-foreground hover:text-primary font-semibold'
              : 'text-muted-foreground/50 hover:text-muted-foreground'
          }`}
        >
          <RotateCcw className="h-4 w-4 mb-0.5" />
          <span className="text-[11px] font-medium">Clear</span>
        </button>

        {/* Next / Submit */}
        {safeCurrentIndex === questions.length - 1 ? (
          <button
            type="button"
            onClick={() => setSubmitDialogOpen(true)}
            className="flex flex-col items-center justify-center py-2.5 px-1 bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors"
          >
            <Send className="h-4 w-4 mb-0.5" />
            <span className="text-[11px] font-bold">Submit</span>
          </button>
        ) : (
          <button
            type="button"
            disabled={!hasNextUnlocked}
            onClick={goToNextQuestion}
            className="flex flex-col items-center justify-center py-2.5 px-1 bg-primary/10 text-primary font-bold hover:bg-primary/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="h-4 w-4 mb-0.5" />
            <span className="text-[11px] font-bold">Next</span>
          </button>
        )}
      </div>

      {/* Mobile Drawer Question Palette (Modal Overlay for Mobile - Mockup 1) */}
      {paletteDrawerOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden flex flex-col justify-end bg-black/60 backdrop-blur-xs transition-opacity"
          onClick={() => setPaletteDrawerOpen(false)}
        >
          <div
            className="mt-auto bg-card rounded-t-3xl border-t border-border p-4 sm:p-5 space-y-3.5 max-h-[88vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Bar: Title & Close Button */}
            <div className="flex items-center justify-between shrink-0">
              <h2 className="font-bold text-base sm:text-lg text-foreground">
                Question palette
              </h2>
              <button
                type="button"
                onClick={() => setPaletteDrawerOpen(false)}
                className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Legend (2 rows, 2 columns) */}
            <div className="grid grid-cols-2 gap-1.5 text-xs shrink-0 pt-0.5">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/25">
                <span className="h-3 w-3 rounded-md bg-primary text-[9px] font-bold text-primary-foreground flex items-center justify-center shrink-0">✓</span>
                <span className="font-semibold text-xs text-foreground truncate">Answered ({answeredCount}{isTestAttemptCapped && attempt.test.max_attempts ? ` / ${attempt.test.max_attempts}` : ''})</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-tertiary/10 border border-tertiary/25">
                <span className="h-3 w-3 rounded-md bg-tertiary/20 border border-tertiary/50 text-[9px] font-bold text-tertiary flex items-center justify-center shrink-0">?</span>
                <span className="font-semibold text-xs text-foreground truncate">Unanswered ({unansweredCount > 0 ? unansweredCount : 0})</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-secondary/10 border border-secondary/25">
                <span className="h-3 w-3 rounded-md bg-secondary text-[9px] font-bold text-secondary-foreground flex items-center justify-center shrink-0">★</span>
                <span className="font-semibold text-xs text-foreground truncate">Marked ({markedCount})</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/40 border border-border/50">
                <span className="h-3 w-3 rounded-md bg-muted border border-border/70 text-[9px] text-muted-foreground flex items-center justify-center shrink-0">-</span>
                <span className="font-semibold text-xs text-muted-foreground truncate">Not Visited ({notVisitedCount})</span>
              </div>
            </div>

            {/* Section Header & Tabs */}
            {attempt.test.sections && attempt.test.sections.length > 1 ? (
              <div className="space-y-1.5 shrink-0 pt-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                  <span>{activePaletteSection?.name ?? 'SECTION'}</span>
                  <span>{mobilePaletteQuestions.length} QUESTIONS</span>
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                  {attempt.test.sections.map(sec => {
                    const isSecActive = (drawerSectionId ?? currentSectionId) === sec.id
                    const isSecLocked = sectionStates[String(sec.id)]?.locked === true
                    const secQs = questions.filter(q => q.section_id === sec.id)
                    const secAnswered = secQs.filter(q => {
                      const a = answers[q.id]
                      return (a?.selected_option_ids?.length ?? 0) > 0 || (a?.numerical_response != null && !isNaN(a?.numerical_response))
                    }).length

                    return (
                      <button
                        key={sec.id}
                        type="button"
                        disabled={isSecLocked}
                        onClick={() => {
                          if (isSecLocked) return
                          setDrawerSectionId(sec.id)
                          const firstIdx = questions.findIndex(q => q.section_id === sec.id)
                          if (firstIdx !== -1) setSelectedPaletteIndex(firstIdx)
                        }}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold shrink-0 transition-colors flex items-center gap-1 border ${
                          isSecActive
                            ? 'bg-primary text-primary-foreground border-primary shadow-2xs'
                            : isSecLocked
                            ? 'bg-muted/40 text-muted-foreground/40 border-border/40 cursor-not-allowed'
                            : 'bg-card text-foreground hover:bg-muted/60 border-border/70'
                        }`}
                      >
                        {isSecLocked && <Lock className="h-3 w-3 text-destructive" />}
                        <span>{sec.name}</span>
                        <span className="text-[10px] opacity-80">({secAnswered}/{secQs.length})</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground shrink-0 pt-1">
                {effectiveCurrentSection?.name
                  ? `${effectiveCurrentSection.name} · ${mobilePaletteQuestions.length} QUESTIONS`
                  : `ALL QUESTIONS · ${questions.length} QUESTIONS`}
              </div>
            )}

            {/* 5-Column Question Grid */}
            <div className="grid grid-cols-5 gap-2.5 overflow-y-auto flex-1 p-0.5 auto-rows-max min-h-[160px] max-h-[50vh]">
              {renderMobilePaletteItems()}
            </div>

            {/* Bottom CTA: Go to question X */}
            {(() => {
              const targetIdx = (selectedPaletteIndex >= 0 && selectedPaletteIndex < questions.length)
                ? selectedPaletteIndex
                : (safeCurrentIndex >= 0 && safeCurrentIndex < questions.length ? safeCurrentIndex : 0)
              return (
                <div className="pt-2 border-t border-border shrink-0">
                  <Button
                    variant="outline"
                    className="w-full h-12 rounded-xl border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 font-bold text-sm sm:text-base transition-all shadow-2xs"
                    onClick={() => {
                      setCurrentIndex(targetIdx)
                      setPaletteDrawerOpen(false)
                    }}
                  >
                    Go to question {targetIdx + 1}
                  </Button>
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* Submit Confirmation Dialog */}
      <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <DialogContent className="max-w-md p-5 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" /> Submit Test Confirmation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1 text-sm">
            <p className="text-muted-foreground text-xs sm:text-sm">Are you sure you want to submit your test for final evaluation?</p>
            
            {/* Stats Summary Grid */}
            <div className="grid grid-cols-2 gap-2 text-xs bg-muted/40 p-3.5 rounded-xl border border-border/40 font-medium">
              <div className="flex flex-col space-y-0.5">
                <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Total Questions</span>
                <span className="font-extrabold text-sm text-foreground">{questions.length}</span>
              </div>
              <div className="flex flex-col space-y-0.5">
                <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Answered</span>
                <span className="font-extrabold text-sm text-primary">
                  {answeredCount}
                  {isTestAttemptCapped && attempt.test.max_attempts ? (
                    <span className="text-[11px] font-normal text-muted-foreground ml-1">
                      (max {attempt.test.max_attempts})
                    </span>
                  ) : null}
                </span>
              </div>
              <div className="flex flex-col space-y-0.5 pt-1">
                <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Marked Review</span>
                <span className="font-extrabold text-sm text-secondary">{markedCount}</span>
              </div>
              <div className="flex flex-col space-y-0.5 pt-1">
                <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Unanswered</span>
                <span className="font-extrabold text-sm text-tertiary">{unansweredCount > 0 ? unansweredCount : 0}</span>
              </div>
            </div>

            {/* Test-Level Over-Attempt Policy B Evaluation Notice */}
            {isTestAttemptCapped && attempt.test.max_attempts && answeredCount > attempt.test.max_attempts && (
              <div className="rounded-xl border border-secondary/30 bg-secondary/10 p-3.5 space-y-2 text-xs">
                <div className="flex items-center gap-1.5 font-bold text-foreground">
                  <AlertCircle className="h-4 w-4 text-secondary shrink-0" />
                  <span>Important Attempt Evaluation Notice</span>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  You have answered <strong className="text-foreground font-bold">{answeredCount}</strong> questions, which exceeds the permitted maximum of <strong className="text-foreground font-bold">{attempt.test.max_attempts}</strong> attempts for this test.
                </p>
                <div className="p-2.5 rounded-lg bg-card/60 border border-border/60 text-foreground font-medium space-y-1">
                  <p>
                    <strong>Evaluation Rule:</strong> Only the <strong>first {attempt.test.max_attempts} answered questions</strong> (in sequential order 1 to {questions.length}) will be evaluated for scoring.
                  </p>
                  <p className="text-muted-foreground text-[11px]">
                    The remaining {answeredCount - attempt.test.max_attempts} answered question(s) will be ignored and not counted.
                  </p>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  💡 <em>If you want different questions evaluated, click <strong>Continue Test</strong> and use <strong>Clear Response</strong> on questions you do not want evaluated.</em>
                </p>
              </div>
            )}

            {/* Section-Level Over-Attempt Policy B Notice */}
            {(!isTestAttemptCapped || !attempt.test.max_attempts) && attempt.test.sections && attempt.test.sections.some(s => {
              if (!s.max_attempts || s.max_attempts <= 0 || s.max_attempts >= s.total_questions) return false
              const sAnswered = questions.filter(q => q.section_id === s.id && isQuestionAnswered(q.id)).length
              return sAnswered > s.max_attempts
            }) && (
              <div className="rounded-xl border border-secondary/30 bg-secondary/10 p-3.5 space-y-2 text-xs">
                <div className="flex items-center gap-1.5 font-bold text-foreground">
                  <AlertCircle className="h-4 w-4 text-secondary shrink-0" />
                  <span>Section Attempt Evaluation Notice</span>
                </div>
                {attempt.test.sections
                  .filter(s => {
                    if (!s.max_attempts || s.max_attempts <= 0 || s.max_attempts >= s.total_questions) return false
                    const sAnswered = questions.filter(q => q.section_id === s.id && isQuestionAnswered(q.id)).length
                    return sAnswered > s.max_attempts
                  })
                  .map(s => {
                    const sAnswered = questions.filter(q => q.section_id === s.id && isQuestionAnswered(q.id)).length
                    return (
                      <p key={s.id} className="text-muted-foreground leading-relaxed">
                        In <strong>{s.name}</strong>, you answered <strong className="text-foreground font-bold">{sAnswered}</strong> questions (maximum allowed: <strong>{s.max_attempts}</strong>). Only the first <strong>{s.max_attempts}</strong> answered questions in this section will be evaluated.
                      </p>
                    )
                  })}
                <p className="text-[11px] text-muted-foreground">
                  💡 <em>Click <strong>Continue Test</strong> and use <strong>Clear Response</strong> on questions you do not want evaluated.</em>
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
            <Button variant="outline" className="w-full sm:w-auto border-border font-semibold text-xs sm:text-sm h-10" onClick={() => setSubmitDialogOpen(false)}>
              Continue Test
            </Button>
            <Button variant="outline" className="w-full sm:w-auto border-2 border-primary/40 text-primary hover:bg-primary/10 font-bold text-xs sm:text-sm h-10" disabled={submitting} onClick={handleSubmit}>
              {submitting ? 'Submitting...' : 'Confirm Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Instructions Dialog */}
      <Dialog open={showInstructionsModal} onOpenChange={setShowInstructionsModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              {attempt.test.title} — Instructions
            </DialogTitle>
            <DialogDescription className="text-xs">
              Review exam guidelines, candidate instructions, and navigation rules.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2 text-xs">
            {attempt.test.exam_config?.general_instructions_html ? (
              <div className="p-4 rounded-xl border border-primary/30 bg-primary/5 space-y-2">
                <span className="font-bold text-primary flex items-center gap-1.5">
                  <FileText className="h-4 w-4" /> Important Instructions from Examiner
                </span>
                <div
                  className="prose prose-xs dark:prose-invert max-w-none text-foreground/90 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: attempt.test.exam_config.general_instructions_html }}
                />
              </div>
            ) : (
              <div className="p-3.5 rounded-xl border border-border/70 bg-muted/20 text-muted-foreground">
                No custom examiner notes provided for this test.
              </div>
            )}

            <div className="space-y-2 p-3.5 rounded-xl border border-border/70 bg-muted/20">
              <span className="font-bold text-foreground block">General Exam & Navigation Rules</span>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li>Click on any question number in the question palette to navigate directly.</li>
                <li>You can change your response at any time by selecting a different option or clicking <strong>Clear Response</strong>.</li>
                <li>Questions marked for review are flagged for later revisiting.</li>
                <li>Ensure you submit your test before the remaining time expires. Unfinished tests will be automatically submitted upon timer expiry.</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button size="sm" onClick={() => setShowInstructionsModal(false)} className="text-xs font-bold">
              Close Instructions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast Notification Container — Positioned in top-center of header */}
      <Toaster
        viewportClassName="fixed top-2 sm:top-2.5 left-1/2 -translate-x-1/2 sm:bottom-auto sm:right-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-2.5 sm:flex-col z-[100] flex flex-col items-center p-0 max-w-sm sm:max-w-md pointer-events-none"
      />
    </div>
  )
}

